import { cva, VariantProps } from 'class-variance-authority';
import classNamesStyle from 'classnames';
import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../../utils/cn';

const inputVariants = cva('focus:outline-none', {
  variants: {
    size: {
      xs: 'h-4 py-1 px-1.5 text-sm',
      sm: 'h-4 py-2 px-4 text-sm',
      md: 'h-[21px] py-2.5 px-4 text-sm',
      lg: 'h-6 py-3.5 px-5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> &
  VariantProps<typeof inputVariants> & {
    error?: boolean;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    startAdornment?: ReactNode;
  };

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ type, size, error, className, endIcon, startIcon, startAdornment, ...props }, ref) => (
    <div
      className={classNamesStyle(
        'relative flex items-center border border-gray-300 rounded-lg focus-within:!ring-[3px] focus-within:ring-primary-100 focus-within:!border-primary',
        {
          'focus-within:ring-red-200 focus-within:!border-red-500 !border-red-500 text-red-700':
            error,
        },
        className,
      )}
    >
      {startAdornment && (
        <div
          className={classNamesStyle(
            cn(inputVariants({ size })),
            'align-self-center h-fill-available border-r text-gray-500',
          )}
        >
          {startAdornment}
        </div>
      )}
      {startIcon && (
        <div className="absolute inset-y-0 left-0 flex items-center pl-3">{startIcon}</div>
      )}
      <input
        type={type}
        data-1p-ignore="true"
        className={classNamesStyle(
          cn(inputVariants({ size })),
          {
            '!pl-10': !!startIcon,
            '!pr-12': !!endIcon,
            'rounded-l-none border-x-0': startAdornment,
          },
          'w-fill-available box-content focus:outline-none rounded-[inherit] placeholder-gray-500  border-none disabled:bg-gray-50 disabled:text-gray-400',
        )}
        ref={ref}
        {...props}
      />
      {endIcon && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">{endIcon}</div>
      )}
    </div>
  ),
);
